import { useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMood } from '@/hooks/useMood';
import { MiniCalendar } from './MiniCalendar';
import { GoalsWidget } from './GoalsWidget';
import { DeadlinesWidget } from './DeadlinesWidget';
import { HabitsWidget } from './HabitsWidget';
import { WeeklyReviewWidget } from './WeeklyReviewWidget';
import { MoodCheckInWidget } from './MoodCheckInWidget';
import { BodyweightWidget } from './BodyweightWidget';
import { cn } from '@/lib/utils';

interface TodaySidebarProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  /**
   * Open a task's detail dialog. Threaded from TodayView rather than each
   * widget owning a dialog, so there is one instance and one close path.
   */
  onOpenTask?: (taskId: string) => void;
  className?: string;
}

export function TodaySidebar({ selectedDate, onDateSelect, onOpenTask, className }: TodaySidebarProps) {
  const { sidebarPreferences, toggleSidebar, toggleWidgetCollapsed, isWidgetCollapsed } = useMood();

  // Memoize toggle callbacks to prevent re-renders
  const toggleCalendar = useCallback(() => toggleWidgetCollapsed('calendar'), [toggleWidgetCollapsed]);
  const toggleGoals = useCallback(() => toggleWidgetCollapsed('goals'), [toggleWidgetCollapsed]);
  const toggleDeadlines = useCallback(() => toggleWidgetCollapsed('deadlines'), [toggleWidgetCollapsed]);
  const toggleHabits = useCallback(() => toggleWidgetCollapsed('habits'), [toggleWidgetCollapsed]);
  const toggleReview = useCallback(() => toggleWidgetCollapsed('review'), [toggleWidgetCollapsed]);
  const toggleMood = useCallback(() => toggleWidgetCollapsed('mood'), [toggleWidgetCollapsed]);
  const toggleBodyweight = useCallback(() => toggleWidgetCollapsed('bodyweight'), [toggleWidgetCollapsed]);

  if (!sidebarPreferences.visible) {
    // Collapsed state - show expand button
    return (
      <div className={cn("flex items-start pt-4", className)}>
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleSidebar}
          className="h-8 w-8 p-0 hover:bg-accent"
          title="Expand sidebar"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className={cn("w-full shrink-0 space-y-4 md:w-80 md:overflow-y-auto md:max-h-[calc(100vh-8rem)]", className)}>
      {/* Collapse button */}
      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleSidebar}
          className="h-8 w-8 p-0 hover:bg-accent"
          title="Collapse sidebar"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Mini Calendar */}
      <MiniCalendar
        selectedDate={selectedDate}
        onDateSelect={onDateSelect}
        isCollapsed={isWidgetCollapsed('calendar')}
        onToggleCollapse={toggleCalendar}
      />

      {/* Goals Widget */}
      <GoalsWidget
        isCollapsed={isWidgetCollapsed('goals')}
        onToggleCollapse={toggleGoals}
      />

      {/* Deadlines Widget */}
      <DeadlinesWidget
        isCollapsed={isWidgetCollapsed('deadlines')}
        onToggleCollapse={toggleDeadlines}
        onOpenTask={onOpenTask}
      />

      {/* Habits Widget */}
      <HabitsWidget
        isCollapsed={isWidgetCollapsed('habits')}
        onToggleCollapse={toggleHabits}
      />

      {/* Weekly Review Widget */}
      <WeeklyReviewWidget
        isCollapsed={isWidgetCollapsed('review')}
        onToggleCollapse={toggleReview}
      />

      {/* Mood Check-in Widget */}
      <MoodCheckInWidget
        isCollapsed={isWidgetCollapsed('mood')}
        onToggleCollapse={toggleMood}
      />

      {/* Bodyweight — the morning weigh-in, same record as Fitness */}
      <BodyweightWidget
        isCollapsed={isWidgetCollapsed('bodyweight')}
        onToggleCollapse={toggleBodyweight}
      />
    </div>
  );
}
